import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Eye, Filter, ArrowUpAZ, ArrowDownAZ, Check, X } from "lucide-react";
import { getAllOrderDetail, createBulkOrder, getOrderById, updateOrderItemStatus, updateOrderStatus } from "../../reducers/order.slice";
import { convertDateToCT, formatNumberToDollar, countActiveFiltersAndSort } from "../../utils/util";
import { AuthContext } from "../../context/authContext";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import PageHeader from "../../components/shared/PageHeader";
import SearchBar from "../../components/shared/SearchBar";
import RefreshButton from "../../components/shared/RefreshButton";
import StatusBadge from "../../components/shared/StatusBadge";
import useMediaQuery from "../../components/shared/useMediaQuery";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
} from "../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import DataTable from "../../components/shared/DataTable";

const ORDER_TYPE_OPTIONS = [
  { value: "PRE_QUOTE", label: "Sell" },
  { value: "QUOTE", label: "Quote" },
  { value: "SHIP_DIRECT", label: "Ship Direct" },
];
const ORDER_TYPES = ORDER_TYPE_OPTIONS.map((option) => option.value);

const getOrderTypeLabel = (type) =>
  ORDER_TYPE_OPTIONS.find((option) => option.value === type)?.label ||
  type?.replace(/_/g, " ") ||
  "—";

const CHUNK_SIZE = 50;
const TAB_OPTIONS = [
  { key: "all", label: "All", statuses: [] },
  { key: "pending", label: "Pending Approval", statuses: ["PENDING_APPROVAL"] },
  { key: "approved", label: "Approved", statuses: ["APPROVED"] },
  { key: "rejected", label: "Rejected", statuses: ["REJECTED"] },
];

const tabKeyFromStatuses = (statuses = []) => {
  const match = TAB_OPTIONS.find(
    (tab) =>
      tab.statuses.length === statuses.length &&
      tab.statuses.every((status) => statuses.includes(status))
  );
  return match?.key || "all";
};

const getInitialTab = () => {
  const params = new URLSearchParams(window.location.search);
  const queryStatus = params.get("status");
  if (queryStatus === "approved") return "approved";
  if (queryStatus === "pending") return "pending";
  if (queryStatus === "rejected") return "rejected";
  if (window.location.pathname.includes("/dashboard/completed-orders")) return "approved";

  const storedTab = sessionStorage.getItem("liveOrders_activeTab");
  if (TAB_OPTIONS.some((tab) => tab.key === storedTab)) return storedTab;

  const storedStatuses = sessionStorage.getItem("liveOrders_selectedStatuses");
  return storedStatuses ? tabKeyFromStatuses(JSON.parse(storedStatuses)) : "all";
};

const getStatusesForTab = (tabKey) => TAB_OPTIONS.find((tab) => tab.key === tabKey)?.statuses || [];

const statStyle = (accent, isMobile = false) => ({
  background: "linear-gradient(180deg, rgba(255,255,255,0.025), transparent)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderTop: `3px solid ${accent}`,
  borderRadius: isMobile ? 10 : 14,
  padding: isMobile ? "10px 8px" : "16px 18px",
  textAlign: isMobile ? "center" : "left",
});

const statLabelStyle = {
  display: "block",
  color: "#c3c8cc",
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: "0.09em",
};

const statValueStyle = {
  display: "block",
  color: "#fff",
  fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
  fontSize: 24,
  fontWeight: 800,
  marginTop: 8,
};

const LiveOrderPage = () => {
  const initialTabRef = useRef(getInitialTab());
  const [showLoader, setShowLoader] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(() => getStatusesForTab(initialTabRef.current));
  const [activeTab, setActiveTab] = useState(initialTabRef.current);
  const [selectedTypes, setSelectedTypes] = useState(() => {
    const s = sessionStorage.getItem("liveOrders_selectedTypes");
    return s ? JSON.parse(s).filter((type) => ORDER_TYPES.includes(type)) : [];
  });
  const [sortDirection, setSortDirection] = useState(() => sessionStorage.getItem("liveOrders_sortDirection") || null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const [notes, setNotes] = useState("");
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [allLoadedData, setAllLoadedData] = useState([]);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loadingChunk, setLoadingChunk] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const s = sessionStorage.getItem("liveOrders_rowsPerPage");
    return s ? JSON.parse(s) : 5;
  });
  const [totalRows, setTotalRows] = useState(0);
  const [isNavigatingToPage, setIsNavigatingToPage] = useState(false);
  const [pageCache, setPageCache] = useState(new Map());
  const [viewOrderId, setViewOrderId] = useState(null);
  const [viewOrderData, setViewOrderData] = useState({});
  const [viewOrderLoading, setViewOrderLoading] = useState(false);
  const [viewRejectOpen, setViewRejectOpen] = useState(false);
  const [viewRejectNotes, setViewRejectNotes] = useState("");
  const [viewRejectItem, setViewRejectItem] = useState({});

  const isInitialRender = useRef(true);
  const location = useLocation();
  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);

  const loadDataChunk = async (chunkNumber = 0, overrides = {}) => {
    const effectiveStatuses = overrides.statuses ?? selectedStatuses;
    const filterKey = JSON.stringify({
      selectedStatuses: [...effectiveStatuses].sort(),
      selectedTypes: [...selectedTypes].sort(),
      search: searchTerm,
      sortDirection,
    });
    const cacheKey = `chunk-${chunkNumber}-${filterKey}`;

    if (!overrides.bypassCache && pageCache.has(cacheKey)) {
      const cached = pageCache.get(cacheKey);
      if (cached.totalRows !== undefined) setTotalRows(cached.totalRows);
      return cached.data;
    }
    if (loadingChunk) return [];

    setLoadingChunk(true);
    if (chunkNumber === 0 && allLoadedData.length === 0) setShowLoader(true);

    try {
      const payload = {
        skip: chunkNumber * CHUNK_SIZE,
        limit: CHUNK_SIZE,
        order_status: effectiveStatuses.length > 0 ? effectiveStatuses : [],
        type: selectedTypes.length > 0 ? selectedTypes : [],
      };
      if (searchTerm?.trim()) payload.search = searchTerm.trim();
      if (sortDirection && sortDirection !== "null") {
        payload.sort_by = "ORDER_PRICE";
        payload.sort_order = sortDirection;
      }

      const res = await dispatch(getAllOrderDetail(payload));
      if (res?.error && Object.keys(res.error).length > 0) {
        showToast(res.error.message, "error");
        setHasMoreData(false);
        setFilteredData([]);
        return [];
      }
      if (!res?.payload?.data || !Array.isArray(res.payload.data)) {
        setHasMoreData(false);
        setFilteredData([]);
        return [];
      }

      if ((chunkNumber === 0 || !totalRows) && res.payload.total) setTotalRows(res.payload.total);

      const formatted = res.payload.data.map((item) => ({
        ...item,
        createdDate: convertDateToCT(item.created_at, "MMMM D, YYYY hh:mm:ss A"),
        approvalDate:
          item.status === "APPROVED" && item.status_updated_at
            ? convertDateToCT(item.status_updated_at, "MMMM D, YYYY hh:mm:ss A")
            : "—",
        orderPrice: formatNumberToDollar(item.order_price),
        newOrderItems: item.order_items?.map((oi) => ({
          ...oi,
          buy_price: formatNumberToDollar(oi.buy_price || 0),
          cashout_price: formatNumberToDollar(oi.cashout_price || 0),
          total_amount: formatNumberToDollar(oi.total_amount || 0),
        })),
      }));

      setPageCache((prev) => new Map(prev.set(cacheKey, { data: formatted, timestamp: Date.now(), totalRows: res.payload.total })));
      setHasMoreData(formatted.length === CHUNK_SIZE);
      return formatted;
    } catch (err) {
      showToast("Error fetching data", "error");
      setHasMoreData(false);
      return [];
    } finally {
      setLoadingChunk(false);
      setShowLoader(false);
    }
  };

  const loadInitialData = async (overrides = {}) => {
    const chunk = await loadDataChunk(0, overrides);
    setAllLoadedData(chunk || []);
  };

  const checkAndLoadMoreData = async (page, rpp) => {
    const required = (page + 1) * rpp;
    if (allLoadedData.length >= required || !hasMoreData || loadingChunk) return;
    if (totalRows > 0 && allLoadedData.length >= totalRows) { setHasMoreData(false); return; }
    try {
      setIsNavigatingToPage(true);
      const nextChunk = Math.floor(allLoadedData.length / CHUNK_SIZE);
      const newData = await loadDataChunk(nextChunk);
      if (newData?.length > 0) {
        setAllLoadedData((prev) => [...prev, ...newData]);
        setTimeout(() => {
          const combined = [...allLoadedData, ...newData];
          setCurrentPage(page);
          setFilteredData(combined.slice(page * rpp, page * rpp + rpp));
        }, 100);
      } else {
        setHasMoreData(false);
      }
    } finally {
      setIsNavigatingToPage(false);
    }
  };

  const handleChangePage = async (newPage) => {
    if (isNavigatingToPage || loadingChunk) return;
    try {
      setIsNavigatingToPage(true);
      const required = (newPage + 1) * rowsPerPage;
      if (allLoadedData.length < required && hasMoreData) {
        await checkAndLoadMoreData(newPage, rowsPerPage);
      } else {
        setCurrentPage(newPage);
        setFilteredData(allLoadedData.slice(newPage * rowsPerPage, newPage * rowsPerPage + rowsPerPage));
      }
    } finally {
      setIsNavigatingToPage(false);
    }
  };

  const handleChangeRowsPerPage = (newRpp) => {
    setRowsPerPage(newRpp);
    sessionStorage.setItem("liveOrders_rowsPerPage", JSON.stringify(newRpp));
    setCurrentPage(0);
    setSelectedRows([]);
    setSelectionResetKey((key) => key + 1);
    if (allLoadedData.length < newRpp && hasMoreData && !loadingChunk) {
      checkAndLoadMoreData(0, newRpp);
    }
  };

  const applyFilters = async (overrides = {}) => {
    const effectiveStatuses = overrides.statuses ?? selectedStatuses;
    setLoadingChunk(true);
    setCurrentPage(0);
    setAllLoadedData([]);
    sessionStorage.setItem("liveOrders_selectedStatuses", JSON.stringify(effectiveStatuses));
    sessionStorage.setItem("liveOrders_activeTab", tabKeyFromStatuses(effectiveStatuses));
    sessionStorage.setItem("liveOrders_selectedTypes", JSON.stringify(selectedTypes));
    sessionStorage.setItem("liveOrders_sortDirection", sortDirection);
    const results = await loadDataChunk(0, { statuses: effectiveStatuses });
    if (results?.length > 0) setAllLoadedData(results);
    else { setFilteredData([]); setAllLoadedData([]); }
    setLoadingChunk(false);
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab.key);
    setSelectedStatuses(tab.statuses);
    applyFilters({ statuses: tab.statuses });
  };

  const handleRefresh = () => {
    setPageCache(new Map());
    setAllLoadedData([]);
    setHasMoreData(true);
    setSelectedRows([]);
    setSelectionResetKey((key) => key + 1);
    loadInitialData({ bypassCache: true });
  };

  const handleApproveOrder = () => {
    const targetRows = selectedRows.filter((r) => r.status === "PENDING_APPROVAL");
    if (targetRows.length === 0) {
      setBulkRejectOpen(false);
      setBulkAction(null);
      clearSelectedRows();
      showToast("Only pending orders can be approved", "warning");
      return;
    }
    setBulkAction("approve");
    setShowLoader(true);
    const structuredPayload = targetRows.map((row) => ({
      order_id: row.id,
      status: "APPROVED",
      order_items: row.order_items?.map((item) => ({ id: item.id, status: "APPROVED" })),
    }));
    dispatch(createBulkOrder(structuredPayload)).then((res) => {
      if (res?.error && Object.keys(res.error).length > 0) {
        setShowLoader(false);
        setBulkAction(null);
        showToast(res.error.message, "error");
        return;
      }
      setShowLoader(false);
      setBulkAction(null);
      setBulkRejectOpen(false);
      const approvedIds = new Set(targetRows.map((r) => r.id));
      setSelectedRows([]);
      setSelectionResetKey((key) => key + 1);
      showToast("Orders approved successfully", "success");

      setPageCache(new Map());

      // Optimistic real-time state update
      setAllLoadedData((prev) => {
        if (activeTab === "pending") {
          return prev.filter((r) => !approvedIds.has(r.id));
        }
        return prev.map((r) =>
          approvedIds.has(r.id) ? { ...r, status: "APPROVED" } : r
        );
      });
      setFilteredData((prev) => {
        if (activeTab === "pending") {
          return prev.filter((r) => !approvedIds.has(r.id));
        }
        return prev.map((r) =>
          approvedIds.has(r.id) ? { ...r, status: "APPROVED" } : r
        );
      });
      if (activeTab === "pending") {
        setTotalRows((prev) => Math.max(0, prev - approvedIds.size));
      }

      loadInitialData({ bypassCache: true });
    });
  };

  const handleRejectOrder = () => {
    const targetRows = selectedRows.filter((r) => r.status === "PENDING_APPROVAL");
    if (targetRows.length === 0) {
      setBulkRejectOpen(false);
      setBulkAction(null);
      setNotes("");
      clearSelectedRows();
      showToast("Only pending orders can be rejected", "warning");
      return;
    }
    setBulkAction("reject");
    setShowLoader(true);
    const structuredPayload = targetRows.map((row) => ({
      order_id: row.id,
      status: "REJECTED",
      ...(notes?.trim() ? { notes } : {}),
      order_items: row.order_items?.map((item) => ({
        id: item.id,
        status: "REJECTED",
        ...(notes?.trim() ? { notes } : {}),
      })),
    }));
    dispatch(createBulkOrder(structuredPayload)).then((res) => {
      if (res?.error && Object.keys(res.error).length > 0) {
        setShowLoader(false);
        setBulkAction(null);
        showToast(res.error.message, "error");
        return;
      }
      setShowLoader(false);
      setBulkAction(null);
      setBulkRejectOpen(false);
      const rejectedIds = new Set(targetRows.map((r) => r.id));
      setSelectedRows([]);
      setSelectionResetKey((key) => key + 1);
      setNotes("");
      showToast("Orders rejected successfully", "success");

      setPageCache(new Map());

      // Optimistic real-time state update
      setAllLoadedData((prev) => {
        if (activeTab === "pending") {
          return prev.filter((r) => !rejectedIds.has(r.id));
        }
        return prev.map((r) =>
          rejectedIds.has(r.id) ? { ...r, status: "REJECTED" } : r
        );
      });
      setFilteredData((prev) => {
        if (activeTab === "pending") {
          return prev.filter((r) => !rejectedIds.has(r.id));
        }
        return prev.map((r) =>
          rejectedIds.has(r.id) ? { ...r, status: "REJECTED" } : r
        );
      });
      if (activeTab === "pending") {
        setTotalRows((prev) => Math.max(0, prev - rejectedIds.size));
      }

      loadInitialData({ bypassCache: true });
    });
  };

  useEffect(() => { loadInitialData(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryStatus = params.get("status");
    if (!queryStatus) return;

    const tab = TAB_OPTIONS.find((option) => option.key === queryStatus);
    if (!tab || tab.key === activeTab) return;

    setActiveTab(tab.key);
    setSelectedStatuses(tab.statuses);
    applyFilters({ statuses: tab.statuses });
  }, [location.search]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (isInitialRender.current) { isInitialRender.current = false; return; }
    if (isSearching) return;
    const run = async () => {
      setIsSearching(true);
      setCurrentPage(0);
      setAllLoadedData([]);
      const results = await loadDataChunk(0);
      if (results?.length > 0) setAllLoadedData(results);
      else { setFilteredData([]); setAllLoadedData([]); }
      setIsSearching(false);
    };
    run();
  }, [debouncedSearch]);

  useEffect(() => {
    if (isNavigatingToPage || loadingChunk) return;
    if (!allLoadedData || allLoadedData.length === 0) {
      setFilteredData([]);
      return;
    }
    const start = currentPage * rowsPerPage;
    const pageData = allLoadedData.slice(start, start + rowsPerPage);
    if (pageData.length > 0) {
      setFilteredData(pageData);
    } else if (currentPage > 0) {
      setCurrentPage((p) => p - 1);
    } else {
      setFilteredData([]);
    }
  }, [rowsPerPage, allLoadedData, currentPage, isNavigatingToPage, loadingChunk]);

  const fetchViewOrder = (orderId) => {
    setViewOrderLoading(true);
    dispatch(getOrderById({ id: orderId })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setViewOrderLoading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const newOrderItems = res?.payload?.order_items?.map((item) => ({
        ...item,
        buy_price: formatNumberToDollar(item?.buy_price),
        cashout_price: formatNumberToDollar(item?.cashout_price),
        total_amount: formatNumberToDollar(item?.total_amount),
      }));
      setViewOrderData({
        ...res?.payload,
        newOrderItems,
        createdDate: convertDateToCT(res?.payload?.created_at, "MMMM D, YYYY hh:mm:ss A"),
        orderPrice: formatNumberToDollar(res?.payload?.order_price),
      });
      setViewOrderLoading(false);
    });
  };

  const handleViewLiveOrder = (row) => {
    setViewOrderId(row.id);
    setViewOrderData({});
    fetchViewOrder(row.id);
  };

  const handleViewApproveItem = (row) => {
    setViewOrderLoading(true);
    dispatch(updateOrderItemStatus({ id: row?.id, status: "APPROVED" })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setViewOrderLoading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setViewOrderLoading(false);
      fetchViewOrder(viewOrderId);
      showToast("Changes saved successfully", "success");

      setPageCache(new Map());
      setAllLoadedData((prev) =>
        prev.map((order) => {
          if (order.id !== viewOrderId) return order;
          const updatedItems = order.order_items?.map((it) =>
            it.id === row.id ? { ...it, status: "APPROVED" } : it
          );
          const updatedNewItems = order.newOrderItems?.map((it) =>
            it.id === row.id ? { ...it, status: "APPROVED" } : it
          );
          return { ...order, order_items: updatedItems, newOrderItems: updatedNewItems };
        })
      );
      loadInitialData({ bypassCache: true });
    });
  };

  const handleViewOpenReject = (row) => {
    setViewRejectItem(row);
    setViewRejectNotes("");
    setViewRejectOpen(true);
  };

  const handleViewRejectItem = () => {
    setViewOrderLoading(true);
    dispatch(updateOrderItemStatus({ id: viewRejectItem?.id, status: "REJECTED", notes: viewRejectNotes })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setViewOrderLoading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setViewOrderLoading(false);
      setViewRejectOpen(false);
      fetchViewOrder(viewOrderId);
      showToast("Changes saved successfully", "success");

      setPageCache(new Map());
      setAllLoadedData((prev) =>
        prev.map((order) => {
          if (order.id !== viewOrderId) return order;
          const updatedItems = order.order_items?.map((it) =>
            it.id === viewRejectItem.id ? { ...it, status: "REJECTED" } : it
          );
          const updatedNewItems = order.newOrderItems?.map((it) =>
            it.id === viewRejectItem.id ? { ...it, status: "REJECTED" } : it
          );
          return { ...order, order_items: updatedItems, newOrderItems: updatedNewItems };
        })
      );
      loadInitialData({ bypassCache: true });
    });
  };

  const handleViewUpdateStatus = () => {
    setViewOrderLoading(true);
    const viewItems = viewOrderData?.newOrderItems || viewOrderData?.order_items || [];
    const isApproved = viewItems.some((item) => item?.status === "APPROVED");
    const targetStatus = isApproved ? "APPROVED" : "REJECTED";
    const targetId = viewOrderData?.id;

    dispatch(updateOrderStatus({ id: targetId, status: targetStatus })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setViewOrderLoading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setViewOrderLoading(false);
      showToast("Order status updated successfully", "success");

      // Close the modal upon successful save
      setViewOrderId(null);
      setViewOrderData({});

      // Invalidate cache and optimistically update parent table in real-time
      setPageCache(new Map());
      setAllLoadedData((prev) => {
        if (activeTab === "pending") {
          return prev.filter((r) => r.id !== targetId);
        }
        return prev.map((r) =>
          r.id === targetId ? { ...r, status: targetStatus } : r
        );
      });
      setFilteredData((prev) => {
        if (activeTab === "pending") {
          return prev.filter((r) => r.id !== targetId);
        }
        return prev.map((r) =>
          r.id === targetId ? { ...r, status: targetStatus } : r
        );
      });
      if (activeTab === "pending") {
        setTotalRows((prev) => Math.max(0, prev - 1));
      }

      loadInitialData({ bypassCache: true });
    });
  };

  const isMobile = useMediaQuery("(max-width: 640px)");

  const activeFilterCount = countActiveFiltersAndSort(sortDirection, selectedTypes);

  const liveOrderStats = useMemo(() => {
    const pendingApproval = allLoadedData.filter((d) => d.status === "PENDING_APPROVAL").length;
    const approved = allLoadedData.filter((d) => d.status === "APPROVED").length;
    const rejected = allLoadedData.filter((d) => d.status === "REJECTED").length;
    return { pendingApproval, approved, rejected };
  }, [allLoadedData]);

  const pendingSelectedCount = selectedRows.filter(
    (row) => row.status === "PENDING_APPROVAL"
  ).length;
  const canApproveSelected = pendingSelectedCount > 0;

  const clearSelectedRows = () => {
    setSelectedRows([]);
    setSelectionResetKey((key) => key + 1);
  };

  const columns = [
    { key: "id", label: "Order ID", width: 120 },
    { key: "orderPrice", label: "Order Price", width: 100, align: "right" },
    { key: "type", label: "Order Type", width: 110, render: (row) => getOrderTypeLabel(row.type) },
    { key: "created_by_name", label: "Member Name", width: 140 },
    { key: "createdDate", label: "Order Date", width: 180 },
    { key: "approvalDate", label: "Approval Date", width: 180 },
    {
      key: "status",
      label: "Status",
      width: 130,
      align: "center",
      render: (row) => <StatusBadge status={row?.status} />,
    },
    {
      key: "action",
      label: "Action",
      width: 70,
      align: "center",
      render: (row) => (
        <Button variant="ghost" size="icon-sm" onClick={() => handleViewLiveOrder(row)} title="View Details">
          <Eye size={15} />
        </Button>
      ),
    },
  ];

  const itemsColumns = [
    { key: "id", label: "Item ID" },
    { key: "item_name", label: "Item Name" },
    { key: "upc", label: "UPC" },
    { key: "quote_id", label: "Quote ID" },
    { key: "quantity", label: "Quantity" },
    { key: "buy_price", label: "Buy Price" },
    { key: "cashout_price", label: "Cashout Price" },
    { key: "retailer_name", label: "Retailer Name" },
    { key: "total_amount", label: "Total Amount" },
    { key: "notes", label: "Notes" },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge status={row?.status} />,
    },
  ];

  const viewItemsColumns = [
    { key: "item_name", label: "Item Name", width: "28%", maxWidth: 260 },
    { key: "upc", label: "UPC", width: "15%" },
    { key: "quantity", label: "Qty", width: "8%", align: "right" },
    { key: "buy_price", label: "Buy Price", width: "11%", align: "right" },
    { key: "cashout_price", label: "Cashout", width: "11%", align: "right" },
    { key: "total_amount", label: "Total", width: "11%", align: "right" },
    {
      key: "status",
      label: "Status",
      width: "12%",
      align: "center",
      render: (row) => <StatusBadge status={row?.status} />,
    },
    {
      key: "action",
      label: "Action",
      width: "8%",
      align: "center",
      render: (row) => {
        const isPendingItem = row?.status?.includes("PENDING");
        const isApproved = row?.status === "APPROVED" || row?.status?.includes("PAYMENT");
        const isRejected = row?.status === "REJECTED";

        if (!isPendingItem) {
          return <span style={{ color: "#8b9296", fontSize: 13 }}>—</span>;
        }

        return (
          <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
            {!isApproved && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => handleViewApproveItem(row)}
                title="Approve"
                style={{ color: "#4caf50", border: "1px solid #4caf50" }}
              >
                <Check size={14} />
              </Button>
            )}
            {!isRejected && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => handleViewOpenReject(row)}
                title="Reject"
                style={{ color: "#f27059", border: "1px solid #f27059" }}
              >
                <X size={14} />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const viewSummaryFields = [
    { label: "Order Number", value: viewOrderData?.id || "—" },
    { label: "Member Name", value: viewOrderData?.created_by_name || "—" },
    { label: "Order Type", value: getOrderTypeLabel(viewOrderData?.type) },
    { label: "Order Date", value: viewOrderData?.createdDate || "—" },
    { label: "Order Price", value: viewOrderData?.orderPrice || "—" },
  ];

  const viewItems = viewOrderData?.newOrderItems || viewOrderData?.order_items || [];
  const viewHasPendingItems = viewItems.some((item) => item?.status?.includes("PENDING"));

  return (
    <>
      {bulkAction && (
        <div
          role="status"
          aria-live="polite"
          aria-label={bulkAction === "approve" ? "Approving selected orders" : "Rejecting selected orders"}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 70,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(4px)",
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          <div
            style={{
              padding: "12px 24px",
              borderRadius: 10,
              background: "#161616",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              boxShadow: "0 8px 30px rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span>{bulkAction === "approve" ? "Approving orders..." : "Rejecting orders..."}</span>
          </div>
        </div>
      )}

      <div style={{ maxWidth: 1200, margin: "0 auto", width: "100%" }}>
        <div style={{ marginBottom: 20 }}>
          <PageHeader title="Orders" subtitle="Manage order approvals, completed orders, and rejected requests from one place." />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(3, 1fr)" : "repeat(auto-fit, minmax(180px, 1fr))",
            gap: isMobile ? 8 : 16,
            marginBottom: 20,
          }}
        >
          <div style={statStyle("#e8b84b", isMobile)}>
            <span style={{ ...statLabelStyle, fontSize: isMobile ? 9 : 10.5, letterSpacing: isMobile ? "0.04em" : "0.09em", textAlign: isMobile ? "center" : "left" }}>
              {isMobile ? "PENDING" : "PENDING APPROVAL"}
            </span>
            <span style={{ ...statValueStyle, fontSize: isMobile ? 18 : 24, marginTop: isMobile ? 4 : 8, textAlign: isMobile ? "center" : "left" }}>
              {liveOrderStats.pendingApproval}
            </span>
          </div>
          <div style={statStyle("#4caf50", isMobile)}>
            <span style={{ ...statLabelStyle, fontSize: isMobile ? 9 : 10.5, letterSpacing: isMobile ? "0.04em" : "0.09em", textAlign: isMobile ? "center" : "left" }}>
              APPROVED
            </span>
            <span style={{ ...statValueStyle, fontSize: isMobile ? 18 : 24, marginTop: isMobile ? 4 : 8, textAlign: isMobile ? "center" : "left" }}>
              {liveOrderStats.approved}
            </span>
          </div>
          <div style={statStyle("#f27059", isMobile)}>
            <span style={{ ...statLabelStyle, fontSize: isMobile ? 9 : 10.5, letterSpacing: isMobile ? "0.04em" : "0.09em", textAlign: isMobile ? "center" : "left" }}>
              REJECTED
            </span>
            <span style={{ ...statValueStyle, fontSize: isMobile ? 18 : 24, marginTop: isMobile ? 4 : 8, textAlign: isMobile ? "center" : "left" }}>
              {liveOrderStats.rejected}
            </span>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            gap: isMobile ? 12 : 6,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: isMobile ? "center" : "flex-start", width: isMobile ? "100%" : "auto", gap: 8 }}>
            {TAB_OPTIONS.map((tab) => {
              const active = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => handleTabClick(tab)}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 999,
                    fontSize: 11.5,
                    fontWeight: 700,
                    cursor: "pointer",
                    border: active ? "1px solid rgba(0,173,181,0.48)" : "1px solid rgba(255,255,255,0.08)",
                    background: active
                      ? "linear-gradient(180deg, rgba(0,173,181,0.18), rgba(0,173,181,0.08))"
                      : "rgba(255,255,255,0.035)",
                    color: active ? "#5ee5ea" : "#c3c8cc",
                    boxShadow: active ? "inset 0 1px 0 rgba(255,255,255,0.06)" : "none",
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {selectedRows.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: isMobile ? "space-between" : "flex-start",
                gap: isMobile ? 8 : 7,
                padding: isMobile ? "8px 10px" : "5px 8px 5px 10px",
                borderRadius: 12,
                border: "1px solid rgba(0, 173, 181, 0.34)",
                background: "linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.018))",
                boxShadow: "0 14px 34px -28px rgba(0, 173, 181, 0.9), inset 0 1px 0 rgba(255,255,255,0.06)",
                backdropFilter: "blur(10px)",
                width: isMobile ? "100%" : "auto",
                boxSizing: "border-box",
                flexShrink: 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 40,
                    height: 26,
                    padding: "0 6px",
                    borderRadius: 999,
                    background: "linear-gradient(135deg, #00ADB5, #4FD1C5)",
                    color: "#071112",
                    fontWeight: 800,
                    fontSize: 12,
                    boxShadow: "0 8px 18px -10px rgba(0,173,181,0.95)",
                  }}
                >
                  {selectedRows.length}
                </span>
                <span style={{ fontSize: isMobile ? 14 : 13, fontWeight: 800, color: "#fff" }}>
                  selected
                </span>
              </div>

              <div style={{ width: 1, height: 22, background: "rgba(255,255,255,0.16)" }} />

              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {canApproveSelected && (
                  <>
                    <Button
                      size="xs"
                      title="Approve selected orders"
                      onClick={handleApproveOrder}
                      disabled={Boolean(bulkAction)}
                      style={{
                        background: "rgba(0, 173, 181, 0.16)",
                        color: "#7be7e4",
                        border: "1px solid rgba(0, 173, 181, 0.42)",
                        borderRadius: 8,
                        padding: "5px 10px",
                        fontWeight: 800,
                        fontSize: 12.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        height: 30,
                      }}
                    >
                      <Check size={13} strokeWidth={2.5} />
                      <span>Approve</span>
                    </Button>
                    <Button
                      size="xs"
                      title="Reject selected orders"
                      onClick={() => setBulkRejectOpen(true)}
                      disabled={Boolean(bulkAction)}
                      style={{
                        background: "rgba(242, 112, 89, 0.14)",
                        color: "#ffb0a1",
                        border: "1px solid rgba(242, 112, 89, 0.4)",
                        borderRadius: 8,
                        padding: "5px 10px",
                        fontWeight: 800,
                        fontSize: 12.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        height: 30,
                      }}
                    >
                      <X size={13} strokeWidth={2.5} />
                      <span>Reject</span>
                    </Button>
                  </>
                )}
                <button
                  type="button"
                  onClick={clearSelectedRows}
                  title="Clear selection"
                  style={{
                    background: "none",
                    border: "none",
                    color: "#aeb6bb",
                    cursor: "pointer",
                    fontSize: 12,
                    padding: "3px 6px",
                    textDecoration: "underline",
                    marginLeft: 2,
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "stretch" : "center",
              gap: 10,
              flexWrap: "wrap",
              width: isMobile ? "100%" : "auto",
            }}
          >
            {!isMobile && <RefreshButton onRefresh={handleRefresh} spinning={showLoader || loadingChunk} />}
            <div style={{ flex: isMobile ? 1 : "none", minWidth: isMobile ? 0 : undefined }}>
              <SearchBar
                placeholder="Search orders..."
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
              />
            </div>
            <DropdownMenu onOpenChange={(open) => { if (!open) applyFilters(); }}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="default" style={{ width: isMobile ? "100%" : "auto" }}>
                  <Filter size={14} /> Filter & Sort
                  {activeFilterCount > 0 && (
                    <span
                      style={{
                        marginLeft: 4,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 16,
                        height: 16,
                        borderRadius: 8,
                        background: "#00ADB5",
                        color: "#000",
                        fontSize: 10,
                        fontWeight: 800,
                        padding: "0 4px",
                      }}
                    >
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" style={{ minWidth: 220 }}>
                <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
                {ORDER_TYPES.map((type) => (
                  <DropdownMenuCheckboxItem
                    key={type}
                    checked={selectedTypes.includes(type)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(checked) =>
                      setSelectedTypes((prev) =>
                        checked ? [...prev, type] : prev.filter((t) => t !== type)
                      )
                    }
                  >
                    {getOrderTypeLabel(type)}
                  </DropdownMenuCheckboxItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Sort by Price</DropdownMenuLabel>
                <DropdownMenuItem active={sortDirection === "ASC"} onClick={() => setSortDirection((prev) => prev === "ASC" ? null : "ASC")}>
                  <ArrowUpAZ size={14} style={{ marginRight: 6 }} /> Low to High
                </DropdownMenuItem>
                <DropdownMenuItem active={sortDirection === "DESC"} onClick={() => setSortDirection((prev) => prev === "DESC" ? null : "DESC")}>
                  <ArrowDownAZ size={14} style={{ marginRight: 6 }} /> High to Low
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={filteredData}
          nestedColumns={itemsColumns}
          nestedRowKey="newOrderItems"
          nestedTableHeader="Order Items"
          totalRows={totalRows}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          onPageChange={(n) => handleChangePage(n)}
          onRowsPerPageChange={(n) => handleChangeRowsPerPage(n)}
          loading={loadingChunk || isNavigatingToPage}
          minWidth={1030}
          isSelectable
          selectableCondition={["status", "===", "PENDING_APPROVAL"]}
          onSelectionChange={setSelectedRows}
          resetSelection={selectionResetKey}
          getRowStyle={(row) => {
            const isSelected = selectedRows.some((r) => r.id === row.id);
            return isSelected
              ? {
                  background: "linear-gradient(90deg, rgba(0, 173, 181, 0.09), rgba(0, 173, 181, 0.025))",
                  boxShadow: "inset 3px 0 0 #00ADB5",
                }
              : {};
          }}
        />
      </div>

      {/* Bulk order rejection dialog */}
      <Dialog
        open={bulkRejectOpen}
        onOpenChange={(open) => {
          if (bulkAction) return;
          setBulkRejectOpen(open);
          if (!open) setNotes("");
        }}
      >
        <DialogContent style={{ maxWidth: 420 }}>
          <DialogHeader>
            <DialogTitle>Reject Selected Orders</DialogTitle>
            <DialogDescription>
              Add an optional reason before rejecting the {pendingSelectedCount} selected pending order{pendingSelectedCount !== 1 ? "s" : ""}.
            </DialogDescription>
          </DialogHeader>
          <div style={{ marginTop: 8 }}>
            <Label>Rejection reason (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add a reason if you choose Reject"
              rows={4}
              style={{ marginTop: 6 }}
              disabled={Boolean(bulkAction)}
            />
          </div>
          <DialogFooter>
            <Button variant="destructive" disabled={Boolean(bulkAction)} onClick={handleRejectOrder}>
              <X size={14} />
              Reject Orders
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Order Detail Dialog */}
      <Dialog open={Boolean(viewOrderId)} onOpenChange={(open) => { if (!open) { setViewOrderId(null); setViewOrderData({}); } }}>
        <DialogContent
          style={{
            maxWidth: 1040,
            width: isMobile ? "calc(100vw - 16px)" : "min(1040px, calc(100vw - 64px))",
            background: "#111",
          }}
        >
          <DialogHeader>
            <DialogTitle style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              Order Summary
              {viewOrderData?.status && <StatusBadge status={viewOrderData.status} />}
            </DialogTitle>
            <DialogDescription>View and manage order details</DialogDescription>
          </DialogHeader>

          {viewOrderLoading && !viewOrderData?.id ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
              <div style={{ color: "#8b9296", fontSize: 14 }}>Loading order details...</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {/* Summary grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "145px" : "180px"}, 1fr))`,
                  gap: isMobile ? 12 : 16,
                }}
              >
                {viewSummaryFields.map(({ label, value }) => (
                  <div
                    key={label}
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.06)",
                      borderRadius: 14,
                      padding: "16px 18px",
                    }}
                  >
                    <span style={{ display: "block", color: "#c3c8cc", fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.09em", fontWeight: 800 }}>{label}</span>
                    <span style={{ display: "block", color: "#fff", fontSize: 14, fontWeight: 700, marginTop: 8, textTransform: "capitalize" }}>{value}</span>
                  </div>
                ))}
              </div>

              {/* Order items */}
              {viewOrderData?.newOrderItems?.length > 0 && (
                <>
                  <div style={{ color: "#fff", fontSize: 14, fontWeight: 800, marginTop: 2 }}>Order Items</div>
                  <DataTable
                    columns={viewItemsColumns}
                    rows={viewOrderData.newOrderItems}
                    totalRows={viewOrderData.newOrderItems.length}
                    currentPage={0}
                    rowsPerPage={viewOrderData.newOrderItems.length}
                    loading={viewOrderLoading}
                    minWidth={980}
                  />
                </>
              )}

              {/* Save button */}
              {viewOrderData?.status?.includes("PENDING") && (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button onClick={handleViewUpdateStatus} disabled={viewHasPendingItems || viewOrderLoading}>
                    {viewOrderLoading ? "Saving..." : "Save"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Order — Reject Item Dialog */}
      <Dialog open={viewRejectOpen} onOpenChange={setViewRejectOpen}>
        <DialogContent style={{ maxWidth: 400 }}>
          <DialogHeader>
            <DialogTitle>Reject Item</DialogTitle>
            <DialogDescription>
              This action will notify the member of your decision. You can provide a reason below.
            </DialogDescription>
          </DialogHeader>
          <div style={{ marginTop: 8 }}>
            <Label>Reason</Label>
            <Textarea
              value={viewRejectNotes}
              onChange={(e) => setViewRejectNotes(e.target.value)}
              placeholder="Provide a reason here"
              rows={4}
              style={{ marginTop: 6 }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewRejectOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleViewRejectItem}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LiveOrderPage;
